import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Button } from "../../../../../design-system";
import {
  getCommunityReelCreatorConfig,
  saveCommunityReelCreatorConfig,
} from "../../../../../database/community/reelCreatorConfig";
import {
  VideoFormat,
  ReelFormatPromptTemplateMap,
  ThreeStepValidatorPromptTemplateKey,
  ThreeStepValidatorPromptTemplateMap,
} from "../../../plan/dayplan/components/dailyStep/ViralReelCreator.types";
import {
  getDefaultReelFormatPrompts,
  getDefaultThreeStepValidatorPrompts,
} from "../../../plan/dayplan/components/dailyStep/ViralReelCreator.api";
import { VocMeta, VocActionRow } from "./editorShared";

// Community-level prompt overrides for the viral reel creator: per-format
// script prompts plus the three-step validator prompts. Saved to the
// community's reel creator config and picked up by every generation.

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const FieldLabel = styled.div`
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-tertiary);
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Select = styled.select`
  appearance: none;
  height: 32px;
  padding: 0 var(--space-2);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  cursor: pointer;
  max-width: 280px;
`;

const PromptArea = styled.textarea`
  appearance: none;
  width: 100%;
  min-height: 160px;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-mono);
  font-size: 12px;
  line-height: 1.5;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }
`;

const ToggleRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  cursor: pointer;
`;

const VIDEO_FORMATS: { id: VideoFormat; label: string }[] = [
  { id: "hard-truth", label: "Hard truth" },
  { id: "storytelling", label: "Storytelling" },
  { id: "tutorial", label: "Tutorial" },
  { id: "before-after", label: "Before and after" },
  { id: "day-in-life", label: "Day in the life" },
  { id: "ugc-rant", label: "UGC rant" },
  { id: "historic-arc", label: "Historic arc" },
  { id: "expert-revelation", label: "Expert revelation" },
];

const VALIDATOR_KEYS: { id: ThreeStepValidatorPromptTemplateKey; label: string }[] = [
  { id: "storySelection", label: "Story selection" },
  { id: "scriptGeneration", label: "Script generation" },
  { id: "rewrite", label: "Rewrite" },
  { id: "scoring", label: "Scoring" },
];

export const PromptTemplatesEditor: React.FC<{ communityId?: string }> = ({
  communityId,
}) => {
  const [formatPrompts, setFormatPrompts] = useState<ReelFormatPromptTemplateMap>(
    getDefaultReelFormatPrompts()
  );
  const [validatorPrompts, setValidatorPrompts] =
    useState<ThreeStepValidatorPromptTemplateMap>(
      getDefaultThreeStepValidatorPrompts()
    );
  const [useThreeStepValidator, setUseThreeStepValidator] = useState(true);
  const [selectedFormat, setSelectedFormat] = useState<VideoFormat>("hard-truth");
  const [selectedValidatorKey, setSelectedValidatorKey] =
    useState<ThreeStepValidatorPromptTemplateKey>("storySelection");
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!communityId) return;
    getCommunityReelCreatorConfig(communityId)
      .then((data) => {
        if (
          data.reelFormatPromptTemplates &&
          typeof data.reelFormatPromptTemplates === "object"
        ) {
          setFormatPrompts({
            ...getDefaultReelFormatPrompts(),
            ...data.reelFormatPromptTemplates,
          });
        }
        if (
          data.threeStepValidatorPromptTemplates &&
          typeof data.threeStepValidatorPromptTemplates === "object"
        ) {
          setValidatorPrompts({
            ...getDefaultThreeStepValidatorPrompts(),
            ...data.threeStepValidatorPromptTemplates,
          });
        }
        if (typeof data.useThreeStepValidator === "boolean") {
          setUseThreeStepValidator(data.useThreeStepValidator);
        }
      })
      .catch((err) =>
        console.error("Error loading community prompt settings:", err)
      );
  }, [communityId]);

  const handleSave = async () => {
    if (!communityId) return;
    setIsSaving(true);
    setStatus("Saving...");
    try {
      await saveCommunityReelCreatorConfig(communityId, {
        reelFormatPromptTemplates: formatPrompts,
        threeStepValidatorPromptTemplates: validatorPrompts,
        useThreeStepValidator,
      });
      setStatus("Saved.");
    } catch (err) {
      console.error("Error saving community prompt settings:", err);
      setStatus("Error saving. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Root>
      <div>
        <FieldLabel>Reel format prompt</FieldLabel>
        <VocMeta>
          The script prompt used when this format is picked for a viral reel.
        </VocMeta>
      </div>
      <Select
        value={selectedFormat}
        onChange={(e) => setSelectedFormat(e.target.value as VideoFormat)}
      >
        {VIDEO_FORMATS.map((f) => (
          <option key={f.id} value={f.id}>
            {f.label}
          </option>
        ))}
      </Select>
      <PromptArea
        value={formatPrompts[selectedFormat] || ""}
        onChange={(e) =>
          setFormatPrompts((prev) => ({ ...prev, [selectedFormat]: e.target.value }))
        }
      />
      <VocActionRow>
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            setFormatPrompts((prev) => ({
              ...prev,
              [selectedFormat]: getDefaultReelFormatPrompts()[selectedFormat],
            }))
          }
        >
          Reset this format to default
        </Button>
      </VocActionRow>

      <ToggleRow>
        <input
          type="checkbox"
          checked={useThreeStepValidator}
          onChange={(e) => setUseThreeStepValidator(e.target.checked)}
        />
        Use the three step validator for business story reels
      </ToggleRow>

      {useThreeStepValidator && (
        <>
          <div>
            <FieldLabel>Validator prompt</FieldLabel>
            <VocMeta>
              The prompts driving story selection, script generation, rewrite,
              and scoring.
            </VocMeta>
          </div>
          <Select
            value={selectedValidatorKey}
            onChange={(e) =>
              setSelectedValidatorKey(
                e.target.value as ThreeStepValidatorPromptTemplateKey
              )
            }
          >
            {VALIDATOR_KEYS.map((k) => (
              <option key={k.id} value={k.id}>
                {k.label}
              </option>
            ))}
          </Select>
          <PromptArea
            value={validatorPrompts[selectedValidatorKey] || ""}
            onChange={(e) =>
              setValidatorPrompts((prev) => ({
                ...prev,
                [selectedValidatorKey]: e.target.value,
              }))
            }
          />
          <VocActionRow>
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                setValidatorPrompts((prev) => ({
                  ...prev,
                  [selectedValidatorKey]:
                    getDefaultThreeStepValidatorPrompts()[selectedValidatorKey],
                }))
              }
            >
              Reset this prompt to default
            </Button>
          </VocActionRow>
        </>
      )}

      <VocActionRow>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          isLoading={isSaving}
          disabled={!communityId}
        >
          Save prompt settings
        </Button>
        {status && <VocMeta>{status}</VocMeta>}
      </VocActionRow>
    </Root>
  );
};

export default PromptTemplatesEditor;
